import { Benefit } from '@wix/ambassador-pricing-plans-v2-benefit/types';
import { Order } from '@wix/ambassador-pricing-plans-v2-order/types';
import type { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import type { Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { PaymentCompleteResultPublic } from '@wix/cashier-payments-widget/dist/src/sdk/types/PaymentCompleteResult';
import type { FormValues } from '@wix/form-viewer';
import type {
  CheckoutData,
  IntegrationData,
  PlanCustomizationData,
  RestrictedPageParams,
  StatusData,
} from '@wix/pricing-plans-router-utils';
import { ThankYouPageSettings } from '@wix/pricing-plans-utils/client-data';
import type { IUser, WidgetProps } from '@wix/yoshi-flow-editor';
import { ButtonDemo, TabState } from '../constants/settings-events';
import { FormattedSpannedPrice } from '../utils';
import type { ScrollToComp } from '../utils/ecom';
import type { VariantsByPlan } from '../utils/variant-visibility';

export enum CheckoutStage {
  ECOM_CHECKOUT_NAVIGATION_START = 'ecom_checkout_navigation_start',
  CHECKOUT_LOAD_START = 'checkout_page_start_load',
  CHECKOUT_LOADED = 'login-in-checkout_loaded',
  LOGIN_CLICKED = 'auth_method_login',
  SIGNUP_CLICKED = 'auth_method_signup',
  CUSTOM_CHECKOUT_LOADED = 'custom_checkout_component_loaded',
  CUSTOM_CHECKOUT_FORM_VIEWER_FAILED_TO_LOAD = 'custom_checkout_form_viewer_failed_to_load',
  CUSTOM_CHECKOUT_CONTINUE = 'custom_checkout_continue_click',
  CUSTOM_CHECKOUT_SERVER_VALIDATION_FAILED = 'custom_checkout_server_validation_failed',
  PAYMENT_SECTION_LOADED = 'payment_component_loaded',
  START_DATE_CHANGED = 'start_date_changed',
  GUEST_CHECKOUT_CONTINUE = 'guest_checkout_continue',
  GUEST_CHECKOUT_GET_FREE_PLAN = 'guest_checkout_get_free_plan',
  THANK_YOU_PAGE = 'thank_you_page',
  AUTH_COMPLETED = 'auth_completed',
}

export interface TextData {
  ['button-text']?: string;
  ['disclaimer-text']?: string;
  ['free-trial-button-text']?: string;
  ['page-title-text']?: string;
  ['ribbon-text']?: string;
}

export type UserData = Pick<IUser, 'id' | 'loggedIn' | 'role' | 'instance'>;

export type SubPage =
  | { name: 'list'; integrationData: IntegrationData }
  | { name: 'checkout'; checkoutData: CheckoutData }
  | { name: 'customization'; planCustomizationData: PlanCustomizationData }
  | { name: 'status'; statusData: StatusData }
  | { name: 'restricted'; restrictedPageParams: RestrictedPageParams; integrationData: IntegrationData };

export enum ModalType {
  CannotAcceptPayment,
  PurchaseInPreview,
  Upgrade,
  None,
}

export type Modal = AlertModal | UpgradeModal | typeof noModal;

type UpgradeModal = GenericModal<ModalType.Upgrade>;

export const noModal = { type: ModalType.None } as const;

export type AlertModal = GenericModal<ModalType.CannotAcceptPayment> | GenericModal<ModalType.PurchaseInPreview>;

export type GenericModal<T extends ModalType> = { type: T; onClose: () => void };

export type ToastData = { message: string; informational?: boolean };

export interface CommonProps {
  subPage: SubPage;
  shouldUpgrade?: boolean;
  modal: Modal;
  toast?: ToastData;
  tabState: TabState;
  buttonDemo: ButtonDemo;
  areSettingsOpened: boolean;
  instance?: string;
  appInstanceId: string;
  metaSiteId?: string;
  siteOwnerId?: string;
  visitorId?: string;
  hideToast(): void;
  showToast(toast: ToastData): void;
  biUpgradeReferralClick(referralInfo: string): void;
  navigateToHomePage(): void;
  locale?: string;
  fitToContentHeight?: boolean;
  isOnEcom: boolean;
}

export interface ListProps {
  overrideAppTitle?: string;
  overrideAppSubtitle?: string;
  plans: PublicPlan[] | Plan[];
  variantEntries?: VariantsByPlan;
  isVariantSelectionEnabled: boolean;
  plansErrorMessage?: string;
  retryLoadPlans: () => void;
  isRetryLoading?: boolean;
  selectPlan(plan: PublicPlan | Plan): void;
  addToCart(plan: PublicPlan | Plan): void;
  selectedPlanId?: string;
  isMultiPageApp?: boolean;
  hideToast(): void;
  toast?: ToastData;
  isOnEcom: boolean;
  isCartEnabled?: boolean;
  isAddToCartLoading?: boolean;
  id: string;
  scrollToComp?: ScrollToComp;
}

export type PromisifyFn<T extends (...args: any) => any> = (...args: Parameters<T>) => Promise<ReturnType<T>>;
export type CouponInputMode = 'trigger' | 'input' | 'coupon';
export type ErrorMessage = { message: string };
export type OnBeforeStartPaymentFn = (
  orderId: string,
  planId: string,
  options: { startDate?: string; couponCode?: string; formId?: string; formValues?: FormValues },
) => void;
export type CheckoutStatus = { success: boolean };
export type OnGetFullyDiscountedPlanFn = (data: {
  orderId: string;
  plan: PublicPlan | Plan;
  couponCode: string;
  integrationData: IntegrationData;
  startDate?: string;
  guestCheckoutEnabled: boolean;
  formId?: string;
  formValues?: FormValues;
  checkoutFlowId?: string;
}) => void;
export type OnGetFreePlanFn = (data: {
  plan: PublicPlan | Plan;
  integrationData: IntegrationData;
  isGuest: boolean;
  guestEmail?: string;
  guestCheckoutEnabled: boolean;
  formId?: string;
  formValues?: FormValues;
  checkoutFlowId?: string;
}) => void;
export type OnContinueAsGuestFn = (data: {
  plan: PublicPlan | Plan;
  email?: string;
  skipOrderCreation?: boolean;
}) => void;
export interface CheckoutProps {
  purchaseLimitExceeded: boolean;
  user: UserData;
  userEmail?: string;
  selectedPlan: PublicPlan | Plan;
  order?: Order;
  prices: FormattedSpannedPrice[];
  benefits: Benefit[];
  loginOnCheckout(): void;
  signupOnCheckout(): void;
  logout(): void;
  navigateToStatus(params: {
    result: PaymentCompleteResultPublic;
    plan: PublicPlan | Plan;
    order: Order;
    integrationData: IntegrationData;
    guestCheckoutEnabled: boolean;
    checkoutFlowId?: string;
  }): void;
  trackInitiateCheckout(): void;
  trackSelectPayment(id: string): void;
  demoBuyNowClicked(): void;
  biCheckoutStage(state: CheckoutStage): void;
  biPlanPurchased(result: PaymentCompleteResultPublic, order: Order, selectedPlan: PublicPlan | Plan): void;
  updatePriceDetails(planId: string, couponCode?: string): void;
  updatePriceDetailsError?: ErrorMessage;
  couponInputMode: CouponInputMode;
  couponCode: string;
  couponLoading: boolean;
  removeCoupon: (planId: string) => void;
  onBeforeStartPayment: OnBeforeStartPaymentFn;
  validateSubmission: (formValues: FormValues) => Promise<void>;
  onBeforeStartPaymentStatus?: CheckoutStatus;
  updateStartDateError?: ErrorMessage;
  applyCouponError?: ErrorMessage;
  hasCoupons: boolean;
  onGetFreePlan: OnGetFreePlanFn;
  onGetFullyDiscountedPlan: OnGetFullyDiscountedPlanFn;
  zeroPricePlanCheckoutStatus?: CheckoutStatus;
  isCheckoutDataInitialized: boolean;
  onContinueAsGuest: OnContinueAsGuestFn;
  onEditGuestEmail: () => void;
  guestCheckoutEnabled: boolean;
  guestEmail?: string;
  guestEmailConfirmed?: boolean;
  guestEmailError?: 'invalid' | 'already-purchased' | 'required';
  guestCheckoutLoading?: boolean;
  skipAdditionalInfoStep?: boolean;
  integrationData: IntegrationData;
  multilingualCountryCode?: string;
  checkoutFlowId?: string;
}

export interface StatusProps {
  settings?: ThankYouPageSettings;
  successful?: boolean;
  translatedError?: string;
  order?: Order;
  startDate?: string;
  ownerDemo?: boolean;
  integrationData: IntegrationData;
  metaSiteId?: string;
  navigateBackToCheckout(): void;
  navigateFromStatusPage(): void;
  biThankYouPageCtaButtonClick(): void;
  biThankYouPageOnLoad(): void;
  biUpgradeReferralClick(referralInfo: string): void;
  isOrderProvisioned?: boolean;
}

export interface RestrictedProps {
  missingPlan: boolean;
  switchAccounts(): void;
  navigateToPlanList(): void;
}

export type AppProps = CommonProps & ListProps & CheckoutProps & StatusProps & RestrictedProps;

export type AppWidgetProps = WidgetProps<AppProps>;

export enum BenefitBulletType {
  NONE = 0,
  CIRCLE_TICK = 1,
  HOLLOW_CIRLCE_TICK = 2,
  TICK = 3,
  ARROW = 4,
}

export enum PlanButtonType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}
